import { useRef, useState } from 'react';
import { Popover } from 'react-aria-components';
import { css } from '@understory-io/leaf/css';
import { Dialog } from '@understory-io/leaf/dialog';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { getLocalTimeZone, today } from '@internationalized/date';
import { Calendar } from '@understory-io/leaf/calendar';
import { token } from '~/styled-system/tokens';
import { FilterTrigger } from './filter-trigger';

type DateFromFilterProps = {
  /** `YYYY-MM-DD`. */
  value: string;
  onChange: (value: string) => void;
  /** Fired when the calendar opens, for analytics. */
  onOpen?: () => void;
};

/**
 * "From <date>" filter: the earliest event start the list should include.
 *
 * Uses Leaf's `Calendar` as a plain day picker — no availability, so every day
 * from today on is selectable. That is the right semantics here: this is a
 * list start date spanning every experience, not a bookable day for one of
 * them, so "is this day available" has no meaning. `min` blocks past dates,
 * matching the legacy page's `minDate`.
 *
 * Desktop anchors a popover to the trigger; mobile opens the calendar in a
 * dialog with an explicit confirm, mirroring `~/ui/date-picker`.
 */
export function DateFromFilter({
  value,
  onChange,
  onOpen,
}: DateFromFilterProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <FilterTrigger
        ref={triggerRef}
        label={
          <FormattedMessage
            defaultMessage="From"
            description="Label on the start-date filter button in the upcoming events filter bar"
          />
        }
        value={
          <span suppressHydrationWarning>
            <FormattedDate
              value={`${value}T00:00:00`}
              day="numeric"
              month="short"
              year="numeric"
            />
          </span>
        }
        open={open}
        popupKind="dialog"
        onClick={() => {
          setOpen((wasOpen) => {
            if (!wasOpen) onOpen?.();
            return !wasOpen;
          });
        }}
      />

      {open && (
        <DateFromCalendar
          value={value}
          triggerRef={triggerRef}
          onClose={() => setOpen(false)}
          onChange={(date) => {
            onChange(date);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

function DateFromCalendar({
  value,
  triggerRef,
  onClose,
  onChange,
}: {
  value: string;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onChange: (date: string) => void;
}) {
  // Matched once per open, not subscribed: the calendar is dismissed on
  // resize-driven remounts anyway, and this mirrors `~/ui/date-picker`.
  const isTabletUp = window.matchMedia(
    `(min-width: ${token('breakpoints.tablet')})`
  ).matches;

  if (isTabletUp) {
    return (
      <Popover
        triggerRef={triggerRef}
        isOpen
        onOpenChange={(isOpen) => {
          if (!isOpen) onClose();
        }}
        placement="bottom start"
        className={popoverStyles}
      >
        <DayPicker value={value} onChange={onChange} />
      </Popover>
    );
  }

  return <DateFromDialog value={value} onClose={onClose} onChange={onChange} />;
}

function DateFromDialog({
  value,
  onClose,
  onChange,
}: {
  value: string;
  onClose: () => void;
  onChange: (date: string) => void;
}) {
  // Mobile confirms explicitly, so a mis-tap on a small calendar doesn't
  // reload the list.
  const [localDate, setLocalDate] = useState(value);

  return (
    <Dialog
      title={
        <FormattedMessage
          defaultMessage="Show events from"
          description="Heading of the dialog that picks the upcoming events list's start date"
        />
      }
      onClose={onClose}
      primaryAction={{
        appearance: 'primary',
        onClick: () => onChange(localDate),
        children: (
          <FormattedMessage
            defaultMessage="Select"
            description="Confirm button in the upcoming events start-date dialog"
          />
        ),
      }}
    >
      <DayPicker value={localDate} onChange={setLocalDate} />
    </Dialog>
  );
}

function DayPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (date: string) => void;
}) {
  const intl = useIntl();

  return (
    <Calendar
      label={intl.formatMessage({
        defaultMessage: 'Select a date',
        description: 'Accessible label for the start-date filter calendar',
      })}
      value={value}
      onChange={onChange}
      min={today(getLocalTimeZone()).toString()}
    />
  );
}

const popoverStyles = css({
  background: 'surface.raised',
  borderRadius: 'large',
  boxShadow: '[0 4px 24px rgba(0, 0, 0, 0.12)]',
  padding: 'medium',
  outline: 'none',
});
